import { CyclingEvent, PublicOrganizer, displayBundesland } from '@radevents/shared';

/**
 * Titel, Beschreibung und die drei strukturierten Datensätze der Veranstalterseite.
 *
 * Ausgelagert aus `OrganizerDetail`, weil es reine Ableitung aus Veranstalter und Eventliste ist
 * — nichts davon hat mit dem Zustand der Seite zu tun. In der Seite standen die knapp vierzig
 * Zeilen mitten zwischen Upload-Handlern und Briefkopf.
 *
 * Der Inhalt ist unverändert; `window.location.origin` bleibt die Quelle der Adressen, damit
 * lokal und in Produktion dasselbe herauskommt wie bisher.
 */
export function organizerSeo(organizer: PublicOrganizer, events: CyclingEvent[]) {
  const canonicalUrl = `${window.location.origin}/veranstalter/${organizer.id}`;
  const pageTitle = `${organizer.name} – Radsport-Veranstalter | Radsport Events`;
  const pageDescription = `Alle Events von ${organizer.name}${organizer.city ? ` in ${organizer.city}` : ''}${organizer.bundesland ? `, ${displayBundesland(organizer.bundesland)}` : ''} – RTF, Rennrad, Gravel und mehr auf rad-events.net.`;
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'SportsOrganization',
    name: organizer.name,
    url: organizer.website ?? canonicalUrl,
    ...(organizer.logoUrl ? { logo: organizer.logoUrl } : {}),
    ...(organizer.city ? { address: { '@type': 'PostalAddress', addressLocality: organizer.city, addressCountry: 'DE', ...(organizer.postalCode ? { postalCode: organizer.postalCode } : {}), ...(organizer.bundesland ? { addressRegion: displayBundesland(organizer.bundesland) } : {}) } } : {}),
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Radsport Events', item: window.location.origin },
      { '@type': 'ListItem', position: 2, name: 'Veranstalter', item: `${window.location.origin}/veranstalter` },
      { '@type': 'ListItem', position: 3, name: organizer.name, item: canonicalUrl },
    ],
  };
  const eventListSchema = events.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Events von ${organizer.name}`,
    numberOfItems: events.length,
    itemListElement: events.slice(0, 20).map((ev, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Event',
        name: ev.title,
        startDate: ev.eventDate,
        location: { '@type': 'Place', name: ev.locationName ?? ev.city, address: { '@type': 'PostalAddress', addressLocality: ev.city, addressCountry: 'DE' } },
        url: `${window.location.origin}/events/${ev.id}`,
      },
    })),
  } : null;


  return { canonicalUrl, pageTitle, pageDescription, orgSchema, breadcrumbSchema, eventListSchema };
}
